/* eslint-disable */
import { useCallback, useEffect, useRef, useState } from 'react';
import { useShellStore } from './shellStore';

// ============================================================
// useMegaHover — which portal menu is open in the app bar (CLB-067).
// ============================================================
// The menus used to open on CSS :hover and :focus-within. A mouse click left
// focus on the tab (or on the section clicked), so that menu stayed open after
// the pointer left; hovering another tab then showed TWO menus (David 12.10:
// Originals + Sales), and Escape could not close it. Now ONE key for the whole
// bar, moved by events only — never by "is it hovered/focused right now".
//
// Every event records where the pointer is (hover) or which item keyboard focus
// is in (kb), then schedules ONE reconcile that reads the latest of those when
// it fires — so a timer can never act on a stale picture and leave a menu open:
//   nothing open → open after a short intent delay (a fast pass over the bar opens nothing)
//   one open     → switch after a shorter delay   (a slide along the tabs)
//   pointer gone → close after a grace            (the way down into the menu, or a
//                                                  brush past the next tab, never shuts it)
//   a click on any button in an item → close now; that item stays closed until the
//                                      pointer leaves it (the resting pointer must not
//                                      reopen what was just chosen)
//   keyboard focus (:focus-visible only — a mouse click's focus never opens) → open
//   Escape · the bar condensing or returning · the window losing focus ·
//   another shell popover opening → close
// Touch is ignored: a tap goes straight to the portal (sections are in the menu
// drawer), so no sticky hover can leave a menu up. Opening a menu closes any
// other shell popover — one window at a time.
// ============================================================

export const MEGA_OPEN_MS = 70;
export const MEGA_SWITCH_MS = 30;
export const MEGA_CLOSE_MS = 150;
const QUIET_MS = 450; // after the bar condenses or returns, a pointer that has not moved opens nothing

export function useMegaHover() {
  const [openKey, setOpenKey] = useState(null);
  const openRef = useRef(null);
  const hoverRef = useRef(null);
  const kbRef = useRef(null);
  const suppressed = useRef(null);
  const quietUntil = useRef(0);
  const timer = useRef(null);
  const condensed = useShellStore((s) => s.condensed);
  const popOpen = useShellStore((s) => s.open);
  const prevCondensed = useRef(condensed);

  const clear = () => { if (timer.current) { clearTimeout(timer.current); timer.current = null; } };
  const set = useCallback((k) => {
    if (openRef.current === k) return;
    openRef.current = k;
    setOpenKey(k);
    if (k && useShellStore.getState().open) useShellStore.getState().closeAll();
  }, []);
  const close = useCallback(() => { clear(); set(null); }, [set]);

  // what should be open now: the hovered item (unless just chosen, or the bar just moved), else keyboard's
  const target = () => {
    const h = hoverRef.current;
    if (h && h !== suppressed.current && Date.now() >= quietUntil.current) return h;
    return kbRef.current;
  };
  const schedule = () => {
    const t = target();
    const cur = openRef.current;
    if (t === cur) { clear(); return; }
    const ms = !t ? MEGA_CLOSE_MS : cur ? MEGA_SWITCH_MS : MEGA_OPEN_MS;
    clear();
    timer.current = setTimeout(() => { timer.current = null; set(target()); }, ms);
  };

  const itemProps = (key) => ({
    onPointerEnter: (e) => { if (e.pointerType === 'touch') return; hoverRef.current = key; schedule(); },
    // a pointer that rested through the quiet window opens on its next real move
    onPointerMove: (e) => {
      if (e.pointerType === 'touch') return;
      hoverRef.current = key;
      if (!timer.current && target() !== openRef.current) schedule();
    },
    onPointerLeave: (e) => {
      if (e.pointerType === 'touch') return;
      if (hoverRef.current === key) hoverRef.current = null;
      if (suppressed.current === key) suppressed.current = null;
      schedule();
    },
    onFocus: (e) => {
      let visible = false;
      try { visible = !!(e.target && e.target.matches && e.target.matches(':focus-visible')); } catch (_) { visible = false; }
      if (!visible) return;
      kbRef.current = key;
      suppressed.current = null;
      clear();
      set(key);
    },
    onBlur: (e) => {
      const next = e.relatedTarget;
      if (next && e.currentTarget.contains(next)) return;
      if (kbRef.current === key) kbRef.current = null;
      schedule();
    },
    // any button inside the item — the tab, a section, the open arrow, the primary action
    onClickCapture: (e) => {
      if (!e.target.closest || !e.target.closest('button')) return;
      suppressed.current = key;
      kbRef.current = null;
      close();
    },
  });

  // Escape closes; the item stays closed until the pointer leaves it or keyboard focus returns
  useEffect(() => {
    if (!openKey) return undefined;
    const onKey = (e) => { if (e.key === 'Escape') { suppressed.current = openRef.current; kbRef.current = null; close(); } };
    document.addEventListener('keydown', onKey);
    return () => document.removeEventListener('keydown', onKey);
  }, [openKey, close]);

  // the bar slid away or back under a resting pointer — close, open nothing until the pointer moves
  useEffect(() => {
    if (prevCondensed.current === condensed) return;
    prevCondensed.current = condensed;
    quietUntil.current = Date.now() + QUIET_MS;
    hoverRef.current = null;
    kbRef.current = null;
    close();
  }, [condensed, close]);
  // another shell popover opened (a filter list, deadlines, notifications) — one window at a time
  useEffect(() => { if (popOpen) { suppressed.current = hoverRef.current; kbRef.current = null; close(); } }, [popOpen, close]);
  // the window lost focus or the page was hidden — a pointer gone elsewhere sends no leave
  useEffect(() => {
    const off = () => { hoverRef.current = null; kbRef.current = null; close(); };
    const onVis = () => { if (document.hidden) off(); };
    // the pointer left the page (straight up into the browser's tabs, most often) — the
    // item's own leave usually fires too; this makes sure (schedule reads refs only)
    const onOut = (e) => { if (!e.relatedTarget && hoverRef.current) { hoverRef.current = null; schedule(); } };
    window.addEventListener('blur', off);
    document.addEventListener('visibilitychange', onVis);
    document.addEventListener('mouseout', onOut);
    return () => { window.removeEventListener('blur', off); document.removeEventListener('visibilitychange', onVis); document.removeEventListener('mouseout', onOut); clear(); };
  }, [close]);

  return { openKey, itemProps };
}
